import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Alertas,
  AuditoriaCampo,
  AuditoriaQuery,
  CargaInicialBody,
  EmbarqueAtualizacao,
  EmbarqueNovo,
  EmbarqueResposta,
  EmbarquesQuery,
  OrdemAberta,
  RelatorioCarga,
  ResultadoEmbarque,
} from '@pcp/contracts';
import { ApiRequestError, api } from './client';

type Filtros = Partial<Omit<EmbarquesQuery, 'atrasado'>> & { atrasado?: boolean };

export const embarqueKeys = {
  list: (f: Filtros) => ['embarques', 'lista', f] as const,
  one: (nr: string) => ['embarques', 'um', nr] as const,
  ordens: (item: string) => ['embarques', 'ordens', item] as const,
  alertas: ['embarques', 'alertas'] as const,
};

export function useEmbarques(filtros: Filtros) {
  return useQuery({
    queryKey: embarqueKeys.list(filtros),
    queryFn: ({ signal }) =>
      api<EmbarqueResposta[]>('/importacao/embarques', {
        query: { ...filtros, atrasado: filtros.atrasado === undefined ? undefined : String(filtros.atrasado) },
        signal,
      }),
  });
}

export function useEmbarque(nr: string | undefined) {
  return useQuery({
    queryKey: embarqueKeys.one(nr ?? ''),
    enabled: !!nr,
    queryFn: ({ signal }) =>
      api<EmbarqueResposta>(`/importacao/embarques/${encodeURIComponent(nr!)}`, { signal }),
  });
}

export function useOrdens(item: string, enabled = true) {
  return useQuery({
    queryKey: embarqueKeys.ordens(item),
    enabled: enabled && item.trim().length > 0,
    queryFn: ({ signal }) =>
      api<OrdemAberta[]>('/importacao/ordens', { query: { item: item.trim() }, signal }),
  });
}

export function useAlertas(enabled: boolean) {
  return useQuery({
    queryKey: embarqueKeys.alertas,
    enabled,
    queryFn: ({ signal }) => api<Alertas>('/importacao/alertas', { signal }),
  });
}

export function useCriarEmbarque() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmbarqueNovo) =>
      api<ResultadoEmbarque>('/importacao/embarques', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['embarques'] }),
  });
}

export function useAtualizarEmbarque(nr: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmbarqueAtualizacao) =>
      api<ResultadoEmbarque>(`/importacao/embarques/${encodeURIComponent(nr)}`, { method: 'PUT', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['embarques'] }),
  });
}

export function useCargaInicial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, simular }: { body: CargaInicialBody; simular: boolean }) =>
      api<RelatorioCarga>('/importacao/carga-inicial', {
        method: 'POST',
        body,
        query: { simular: String(simular) },
      }),
    onSuccess: (_d, v) => (v.simular ? undefined : qc.invalidateQueries({ queryKey: ['embarques'] })),
  });
}

export function useAuditoria(query: Partial<AuditoriaQuery>) {
  return useQuery({
    queryKey: ['auditoria', query] as const,
    queryFn: ({ signal }) => api<AuditoriaCampo[]>('/auditoria', { query, signal }),
  });
}

/** Baixa a exportação (respeita o perfil: o BFF já remove o financeiro de quem não pode ver). */
export async function baixarExportacao(formato: 'xlsx' | 'csv', filtros: Filtros): Promise<void> {
  const params = new URLSearchParams({ formato });
  for (const [k, v] of Object.entries(filtros)) if (v !== undefined && v !== '') params.set(k, String(v));
  const res = await fetch(`/api/importacao/exportar?${params.toString()}`, { credentials: 'same-origin' });
  if (!res.ok) throw new ApiRequestError(res.status, 'HTTP', 'Não foi possível exportar agora.');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `importacao-em-andamento.${formato}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
